import React from "react";

import { ChevronIcon } from "@packages/ds";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { selectFeatureOrganizationsEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import {
  selectProjectBuildResourcesEnabled,
  selectEnableCustomBackupSchedules
} from "Reducers/project/capabilities";
import {
  gitProjectSelector,
  selectProjectHasIntegrations
} from "Reducers/project/project";
import {
  setProjectContext,
  selectProjectPanelExpanded
} from "Reducers/settingsMenu";
import useOrganizationFromURL from "src/organization/pages/settings/pages/members/hooks/useOrganizationFromURL";
import { useAppSelector } from "Store/hooks";

import { MenuChildrenContainer, MenuHeader, Submenu } from "./Menu.styles";
import { OverriddenLink } from "./ProjectSettingsMenu.styles";

function getCurrentSection(endPath) {
  // We are on a settings section, but not in general
  if (endPath) {
    return endPath.replace("/", "");
  }

  // we are on the general project settings section
  return "general";
}

const ProjectSettingsMenu = () => {
  const expanded = useSelector(selectProjectPanelExpanded);
  const { projectId, organizationId } = useDecodedParams();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const intl = useIntl();

  const rootRoute = `/${organizationId}/${projectId}/-/settings`;
  const endPath = pathname.replace(rootRoute, "");
  const currentSection = getCurrentSection(endPath);

  const organization = useOrganizationFromURL();
  const organizationEnabled = useAppSelector(selectFeatureOrganizationsEnabled);
  const hasIntegrations = useSelectorWithUrlParams(
    selectProjectHasIntegrations
  );
  const project = useSelectorWithUrlParams(gitProjectSelector);
  const showAccessScreens =
    project?.hasLink("#access") ||
    !!organization?.hasLink("members") ||
    !organizationEnabled;
  const isCustomBackupSchedulesEnabled = useSelectorWithUrlParams(
    selectEnableCustomBackupSchedules
  );

  const areBuildResourcesEnabled = useSelectorWithUrlParams(
    selectProjectBuildResourcesEnabled
  );

  return (
    <MenuChildrenContainer expanded={expanded}>
      <MenuHeader
        onClick={() => dispatch(setProjectContext())}
        data-testid="menu-settings-project-btn"
      >
        <h1>{intl.formatMessage({ id: "settings.menu.project_settings" })}</h1>
        <ChevronIcon direction={expanded ? "up" : "down"} />
      </MenuHeader>
      {expanded && (
        <Submenu>
          <li>
            <OverriddenLink
              $isActive={currentSection === "general"}
              to={rootRoute}
              data-testid="menu-settings-general-btn"
            >
              {intl.formatMessage({ id: "settings.menu.general" })}
            </OverriddenLink>
          </li>
          {showAccessScreens && (
            <li>
              <OverriddenLink
                to={`${rootRoute}/access`}
                $isActive={currentSection === "access"}
                data-testid="menu-settings-access-btn"
              >
                {intl.formatMessage({ id: "settings.menu.access" })}
              </OverriddenLink>
            </li>
          )}
          <li>
            <OverriddenLink
              to={`${rootRoute}/certificates`}
              $isActive={currentSection === "certificates"}
              data-testid="menu-settings-certificates-btn"
            >
              {intl.formatMessage({ id: "settings.menu.certificates" })}
            </OverriddenLink>
          </li>
          <li>
            <OverriddenLink
              to={`${rootRoute}/domains`}
              $isActive={currentSection === "domains"}
              data-testid="menu-settings-domains-btn"
            >
              {intl.formatMessage({ id: "settings.menu.domains" })}
            </OverriddenLink>
          </li>
          <li>
            <OverriddenLink
              to={`${rootRoute}/deploykey`}
              $isActive={currentSection === "deploykey"}
              data-testid="menu-settings-deploykey-btn"
            >
              {intl.formatMessage({ id: "settings.menu.deploy_key" })}
            </OverriddenLink>
          </li>
          {hasIntegrations && (
            <li>
              <OverriddenLink
                to={`${rootRoute}/integrations`}
                $isActive={currentSection === "integrations"}
                data-testid="menu-settings-integrations-btn"
              >
                {intl.formatMessage({ id: "settings.menu.integrations" })}
              </OverriddenLink>
            </li>
          )}
          {areBuildResourcesEnabled && (
            <li>
              <OverriddenLink
                to={`${rootRoute}/resources`}
                $isActive={currentSection === "resources"}
                data-testid="menu-settings-resources-btn"
              >
                {intl.formatMessage({ id: "settings.menu.resources" })}
              </OverriddenLink>
            </li>
          )}
          <li>
            <OverriddenLink
              to={`${rootRoute}/variables`}
              $isActive={currentSection === "variables"}
              data-testid="menu-settings-variables-btn"
            >
              {intl.formatMessage({ id: "settings.menu.variables" })}
            </OverriddenLink>
          </li>
          {isCustomBackupSchedulesEnabled && (
            <li>
              <OverriddenLink
                to={`${rootRoute}/backup-schedules`}
                $isActive={currentSection === "backup-schedules"}
                data-testid="menu-settings-backup_schedules-btn"
              >
                {intl.formatMessage({ id: "settings.menu.backup_schedules" })}
              </OverriddenLink>
            </li>
          )}
        </Submenu>
      )}
    </MenuChildrenContainer>
  );
};

export default ProjectSettingsMenu;
